import React from "react";
import translations from "../../data/translations";

const MODAL_STYLES = `
  .feedback-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(30, 25, 15, 0.45);
    z-index: var(--z-modal-backdrop);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    animation: feedbackFadeIn 0.2s ease-out;
  }

  .feedback-modal {
    background: var(--paper-card);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-paper-lg);
    max-width: 420px;
    width: 100%;
    overflow: hidden;
    animation: feedbackPopIn 0.25s var(--ease-in-out);
  }

  .feedback-modal-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-6);
    font-weight: var(--font-semibold);
    font-size: var(--text-lg);
  }

  .feedback-modal-header.is-success {
    background: var(--gradient-success);
    color: var(--success-dark);
  }

  .feedback-modal-header.is-error {
    background: var(--gradient-error);
    color: var(--error-dark);
  }

  .feedback-modal-icon {
    font-size: var(--text-2xl);
    line-height: 1;
  }

  .feedback-modal-footer {
    padding: var(--space-4) var(--space-6);
    display: flex;
    justify-content: flex-end;
  }

  .feedback-modal-button {
    border: none;
    border-radius: var(--radius-md);
    padding: var(--space-3) var(--space-6);
    font-weight: var(--font-medium);
    font-size: var(--text-base);
    color: #fff;
    cursor: pointer;
    box-shadow: var(--shadow-paper-sm);
  }

  .feedback-modal-button.is-success {
    background: var(--success);
  }

  .feedback-modal-button.is-error {
    background: var(--error);
  }

  .feedback-modal-button:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-paper-md);
  }

  @keyframes feedbackFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes feedbackPopIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
`;

const Modal = ({ modalOpen, setModalOpen, modalMessage, language }) => {
  const closeRef = React.useRef(null);
  const isSuccess = modalMessage === translations[language].sent;
  const tone = isSuccess ? "is-success" : "is-error";

  React.useEffect(() => {
    if (!modalOpen) return undefined;
    closeRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, setModalOpen]);

  if (!modalOpen) return null;

  return (
    <>
      <style>{MODAL_STYLES}</style>
      <div
        className="feedback-modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget) setModalOpen(false);
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <div className="feedback-modal">
          <div className={`feedback-modal-header ${tone}`}>
            <span className="feedback-modal-icon" aria-hidden="true">
              {isSuccess ? "✓" : "✕"}
            </span>
            <span id="feedback-modal-title">{modalMessage}</span>
          </div>
          <div className="feedback-modal-footer">
            <button
              ref={closeRef}
              type="button"
              className={`feedback-modal-button ${tone}`}
              onClick={() => setModalOpen(false)}
            >
              {translations[language].close}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
